import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, SolidButton, StatusIndicator } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import { useProfile } from '../../hooks/store';
import { ProvaPracticeStatus, ProvaPracticeTitleOption, ProvaService } from '../../services/provaService';

import './index.scss';

interface ProvaPracticeProps {
    /** When set, shows that user's attempts (profile). */
    userId?: string;
    /** Offer Start Practice (dashboard / league / own profile). */
    showStart?: boolean;
}

const formatScore = (attempt: Core.Models.ProvaAttempt): string => {
    if (typeof attempt.competitiveScore === 'number') return attempt.competitiveScore.toFixed(1).replace(/\.0$/, '');
    if (typeof attempt.overallScore === 'number') return attempt.overallScore.toFixed(1).replace(/\.0$/, '');
    return '—';
};

const statusLabel = (status: string): string => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'Completed';
        case 'launched':
            return 'Ready to play';
        case 'inprogress':
            return 'In progress';
        case 'disqualified':
            return 'Disqualified';
        case 'incomplete':
            return 'Incomplete';
        case 'noshow':
            return 'No show';
        case 'cancelled':
            return 'Cancelled';
        default:
            return status || 'Unknown';
    }
};

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch ((status || '').toLowerCase()) {
        case 'scored':
            return 'success';
        case 'launched':
        case 'inprogress':
            return 'warning';
        case 'disqualified':
        case 'noshow':
        case 'cancelled':
            return 'error';
        default:
            return 'default';
    }
};

const formatWhen = (iso: string): string => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const isOpenAttempt = (status: string): boolean =>
    ['launched', 'inprogress'].includes((status || '').toLowerCase());

const ProvaPractice = (props: ProvaPracticeProps): JSX.Element => {
    const { userId, showStart = !userId } = props;
    const profile = useProfile();
    const isSchoolMember = !!profile?.organizationId;

    const [attempts, setAttempts] = useState<Core.Models.ProvaAttempt[]>([]);
    const [status, setStatus] = useState<ProvaPracticeStatus | undefined>();
    const [loading, setLoading] = useState(true);
    const [startingKey, setStartingKey] = useState<string | undefined>();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [attemptData, statusData] = await Promise.all([
                userId ? ProvaService.listAttemptsForUser(userId) : ProvaService.listMyAttempts(),
                showStart ? ProvaService.getStatus() : Promise.resolve(undefined),
            ]);
            setAttempts(attemptData);
            if (statusData) {
                setStatus({
                    ...statusData,
                    titles: statusData.titles ?? [],
                });
            }
        } catch (e) {
            if (showStart) toast.error(Core.API.getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [showStart, userId]);

    useEffect(() => {
        void load();
    }, [load]);

    const titles = status?.titles ?? [];
    const titleNameByKey = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of titles) map.set(t.titleKey.toLowerCase(), t.name);
        return map;
    }, [titles]);

    const titlesByGame = useMemo(() => {
        const groups = new Map<string, ProvaPracticeTitleOption[]>();
        for (const t of titles) {
            const key = t.gameName || 'Practice';
            const list = groups.get(key) ?? [];
            list.push(t);
            groups.set(key, list);
        }
        return [...groups.entries()];
    }, [titles]);

    const canStart = useMemo(
        () => showStart && isSchoolMember && !!status?.canStart && titles.length > 0,
        [isSchoolMember, showStart, status?.canStart, titles.length]
    );

    const start = useCallback(
        async (titleKey: string) => {
            if (startingKey || !canStart || !titleKey) return;
            setStartingKey(titleKey);
            try {
                const attempt = await ProvaService.startAttempt(titleKey);
                if (attempt.joinUrl) {
                    window.open(attempt.joinUrl, '_blank', 'noopener,noreferrer');
                    toast.success('Practice opened in a new tab. Your score will appear here when you finish.');
                } else {
                    toast.error('Practice started but no join link was returned.');
                }
                await load();
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setStartingKey(undefined);
            }
        },
        [canStart, load, startingKey]
    );

    const heading = showStart ? 'Practice' : 'Practice scores';
    const lede = showStart
        ? status?.message ||
          'Choose a challenge to start. When you finish in Prova, your score shows up here and on your profile.'
        : 'Scores from completed Practice sessions.';

    const emptyMessage = (() => {
        if (showStart && status && !status.available) return status.message;
        if (showStart && !isSchoolMember) return 'Join your school on LeagueSpot to start Practice.';
        if (showStart && titles.length === 0) {
            return status?.message || 'Practice challenges will appear here once they are available.';
        }
        if (showStart) return 'No Practice sessions yet. Start a challenge when you are ready.';
        return 'No Practice scores yet.';
    })();

    const resolveAttemptName = (titleKey?: string) =>
        (titleKey && titleNameByKey.get(titleKey.toLowerCase())) || titleKey || 'Practice session';

    return (
        <div className="prova-practice-wrap">
            <ContentContainer
                className="prova-practice shadow-medium p0"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="prova-practice__header">
                    <h2 className="heading-2 mb0">{heading}</h2>
                    {canStart && titles.length === 1 && (
                        <div className="prova-practice__controls">
                            <SolidButton
                                as="button"
                                size="small"
                                attention
                                onClick={() => void start(titles[0].titleKey)}
                                pending={startingKey === titles[0].titleKey}
                            >
                                Start Practice
                            </SolidButton>
                        </div>
                    )}
                </div>

                <div className="prova-practice__body">
                    {showStart && <p className="prova-practice__lede">{lede}</p>}

                    {canStart && titles.length > 1 && (
                        <div className="prova-practice__catalog">
                            {titlesByGame.map(([gameName, gameTitles]) => (
                                <div key={gameName} className="prova-practice__game-group">
                                    <h3 className="prova-practice__game-name heading-3">{gameName}</h3>
                                    <ul className="prova-practice__challenges">
                                        {gameTitles.map((title) => (
                                            <li key={title.titleKey} className="prova-practice__challenge">
                                                <span className="prova-practice__challenge-name">{title.name}</span>
                                                <SolidButton
                                                    as="button"
                                                    size="small"
                                                    attention
                                                    onClick={() => void start(title.titleKey)}
                                                    pending={startingKey === title.titleKey}
                                                >
                                                    Start
                                                </SolidButton>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {loading ? (
                        <p className="prova-practice__empty">Loading Practice…</p>
                    ) : attempts.length === 0 ? (
                        <div className="prova-practice__empty-state">
                            <p className="prova-practice__empty">{emptyMessage}</p>
                            {canStart && titles.length === 1 && (
                                <SolidButton
                                    as="button"
                                    size="small"
                                    attention
                                    onClick={() => void start(titles[0].titleKey)}
                                    pending={startingKey === titles[0].titleKey}
                                >
                                    Start Practice
                                </SolidButton>
                            )}
                        </div>
                    ) : (
                        <ul className="prova-practice__list">
                            {attempts.map((attempt) => (
                                <li key={attempt.id} className="prova-practice__row">
                                    <div className="prova-practice__row-main">
                                        <span className="prova-practice__title">
                                            {resolveAttemptName(attempt.titleKey)}
                                        </span>
                                        <span className="prova-practice__date">
                                            {formatWhen(attempt.completedAtUtc || attempt.createdUtc)}
                                        </span>
                                    </div>
                                    <StatusIndicator color={statusColor(attempt.status)} outlineOnly>
                                        {statusLabel(attempt.status)}
                                    </StatusIndicator>
                                    <strong className="prova-practice__score">{formatScore(attempt)}</strong>
                                    {showStart && attempt.joinUrl && isOpenAttempt(attempt.status) && (
                                        <div className="prova-practice__actions">
                                            <HollowButton
                                                as="a"
                                                href={attempt.joinUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                            >
                                                Resume
                                            </HollowButton>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </ContentContainer>
        </div>
    );
};

export default ProvaPractice;
